/**
 * ShiptimizeBlockCheckout — PUDO flow for the WooCommerce Blocks checkout.
 *
 * Responsibilities:
 *  - Extract the shipping address from Gutenberg block form fields
 *  - Intercept the WC Blocks shipping-rate selection API call to detect
 *    carrier changes before the page re-renders (single interceptor, here only)
 *  - Read pickup behaviour from WC Blocks cart store meta_data
 *  - Update ShiptimizeState when any of the above changes
 *  - React to state changes to update the UI
 */
import { PICKUP_BEHAVIOUR } from "../state/shiptimize-state.js";

export default class ShiptimizeBlockCheckout {
  /**
   * @param {ShiptimizeState} state
   * @param {ShiptimizeUI}    ui
   * @param {function}        onOpenMap
   */
  constructor(state, ui, onOpenMap) {
    this._state = state;
    this._ui = ui;
    this._onOpenMap = onOpenMap;

    this._lastValidatedCarrierId = null;
    this._lastValidatedRateId    = null;

    // Exposed so ShiptimizeCheckout can read it for bpost geocoding
    this.currentSelectedRate = null;
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  init() {
    this._setupAPIInterceptor();
    this._extractAddress();

    this._startStoreWatcher();

    // Wait for the shipping rates list to be visible
    const shippingPoll = setInterval(() => {
      if (
        jQuery(".wc-block-components-shipping-rates-control__package").is(
          ":visible",
        )
      ) {
        clearInterval(shippingPoll);
        this._setupHandlers();
      }
    }, 500);

    // Wait for the place order button to be visible
    const btnPoll = setInterval(() => {
      if (
        jQuery(".wc-block-components-checkout-place-order-button").is(
          ":visible",
        )
      ) {
        clearInterval(btnPoll);
        this._setupPlaceOrderGuard();
      }
    }, 500);

    this._waitForCartDataThenEvaluate();
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  _setupHandlers() {
    this._extractAddress();
    this._extractInitialCarrierId();
    this.evaluate();

    // Block checkout form changes (address fields)
    jQuery(".wc-block-checkout__form").on("change", () => {
      this._extractAddress();
    });

    jQuery(document.body).on("updated_checkout", () => {
      this._extractAddress();
      this.evaluate();
    });
  }
  _setupAPIInterceptor() {
    const originalFetch = window.fetch;
    const RATE_URL = "/wp-json/wc/store/v1/cart/select-shipping-rate";

    window.fetch = async (...args) => {
      const [resource, config] = args;

      if (typeof resource === "string" && resource.includes(RATE_URL)) {
        try {
          if (config?.body) {
            const body = JSON.parse(config.body);
            if (body.rate_id) {
              const carrierId = this._extractCarrierIdFromMethodId(
                body.rate_id,
              );
              if (carrierId) {
                this._state.set("carrierId", carrierId);
              }
            }
          }

          return await originalFetch(...args);
        } catch (err) {
          return originalFetch(...args);
        }
      }

      return originalFetch(...args);
    };
  }
  _startStoreWatcher() {
    if (!window.wp?.data) return;

    let lastRateId = null;
    let debounceTimer = null;

    window.wp.data.subscribe(() => {
      try {
        // Never interfere while the map is open — the user is actively
        // selecting a pickup point; clearing buttons or re-evaluating here
        // would wipe the map state and make points disappear.
        if (document.querySelector("#sw.open")) return;

        const store = window.wp.data.select("wc/store/cart");
        const cartData = store?.getCartData?.();
        const selected = cartData?.shippingRates?.[0]?.shipping_rates?.find(
          (r) => r.selected,
        );
        const rateId = selected?.rate_id ?? null;

        if (rateId === lastRateId) return; // nothing changed
        lastRateId = rateId;

        // Clear any existing pickup buttons immediately so the old rate's
        // button doesn't linger while React re-renders the rates list.
        this._ui.removeAllPickupButtons();

        // Debounce: wait 150 ms for React to finish re-rendering the DOM
        // before we try to inject the pickup button into the updated rate list.
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => this.evaluate(), 150);
      } catch {}
    });
  }

  /**
   * Guard the place order button at capture phase so we intercept the click
   * before WooCommerce does when pickup is mandatory but not yet selected.
   */
  _setupPlaceOrderGuard() {
    document
      .querySelectorAll(
        ".wc-block-components-checkout-place-order-button, #place_order",
      )
      .forEach((btn) => {
        btn.addEventListener(
          "click",
          (e) => {
            if (
              this._state.get("pickupBehaviour") !== PICKUP_BEHAVIOUR.MANDATORY
            )
              return;
            if (this._state.get("pickupPoint")) return;

            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this._ui.showPickupMandatoryModal(() => this._onOpenMap());
          },
          true, // capture phase — runs before WooCommerce
        );
      });
  }

  /**
   * Wait for the WP blocks cart store to have shipping rate data, then run
   * evaluate(). Falls back to a polling strategy if the store subscription
   * doesn't fire or if wp.data is not available.
   */
  _waitForCartDataThenEvaluate() {
    let evaluated = false;
    let polls = 0;

    if (window.wp?.data) {
      const unsubscribe = window.wp.data.subscribe(() => {
        if (evaluated) return;
        if (this._isCartDataReady()) {
          evaluated = true;
          this.evaluate();
          unsubscribe();
        }
      });
    }

    const poll = setInterval(() => {
      if (evaluated) {
        clearInterval(poll);
        return;
      }
      polls++;

      if (this._isCartDataReady()) {
        evaluated = true;
        clearInterval(poll);
        this.evaluate();
      } else if (polls >= 10) {
        clearInterval(poll);
        this.evaluate(); // timeout — run anyway
      }
    }, 500);
  }

  _isCartDataReady() {
    if (!window.wp?.data) return typeof wc_checkout_params !== "undefined";

    try {
      const store = window.wp.data.select("wc/store/cart");
      if (!store?.getCartData) return typeof wc_checkout_params !== "undefined";

      const cartData = store.getCartData();
      const selectedRate = cartData?.shippingRates?.[0]?.shipping_rates?.find(
        (r) => r.selected,
      );
      if (!selectedRate) return false;

      return (
        selectedRate.meta_data?.some(
          (m) => m.key === "has_pickup" || m.key === "pickup_mandatory",
        ) ?? false
      );
    } catch {
      return typeof wc_checkout_params !== "undefined";
    }
  }

  /**
   * Read the WP blocks cart store, determine carrier ID and pickup behaviour,
   * and update state + UI accordingly.
   *
   * This is the single method that drives all UI decisions for block checkout.
   */
  evaluate() {
    if (!window.wp?.data) return;

    const store = window.wp.data.select("wc/store/cart");
    if (!store?.getCartData) return;

    try {
      const cartData = store.getCartData();
      if (!cartData?.shippingRates?.[0]) return;

      const selectedRate = cartData.shippingRates[0].shipping_rates.find(
        (r) => r.selected,
      );
      if (!selectedRate) return;

      this.currentSelectedRate = selectedRate;

      // ── Carrier ID ────────────────────────────────────────────────────────
      const carrierId = this._extractCarrierIdFromMethodId(
        selectedRate.rate_id,
      );
      if (carrierId && carrierId !== this._state.get("carrierId")) {
        this._state.set("carrierId", carrierId);
      }

      // ── Detect method switch ──────────────────────────────────────────────
      // Compare rate_id, not just carrierId — multiple methods share the same
      // carrier ID (e.g. CTT Flat Rate, CTT Free Shipping, CTT WBS are all
      // carrier 30). Pickup must clear whenever the user picks a different method.
      const rateChanged =
        this._lastValidatedRateId !== null &&
        selectedRate.rate_id !== this._lastValidatedRateId;

      if (rateChanged) {
        this._state.clearPickup();
        this._ui.clearPickupDisplay();
        this._ui.dismissToast();
        this._ui.resetMandatoryToast();
      }

      // ── Pickup behaviour ──────────────────────────────────────────────────
      const mandatoryMeta = selectedRate.meta_data?.find(
        (m) => m.key === "pickup_mandatory",
      );
      const hasPickupMeta = selectedRate.meta_data?.find(
        (m) => m.key === "has_pickup",
      );
      const mandatoryValue = mandatoryMeta?.value ?? "0";

      const behaviour =
        mandatoryValue === "2"
          ? PICKUP_BEHAVIOUR.IMPOSSIBLE
          : mandatoryValue === "1"
            ? PICKUP_BEHAVIOUR.MANDATORY
            : PICKUP_BEHAVIOUR.OPTIONAL;

      this._state.set("pickupBehaviour", behaviour);

      const pickupPoint = this._state.get("pickupPoint");

      // ── Update UI based on behaviour ──────────────────────────────────────
      if (behaviour === PICKUP_BEHAVIOUR.IMPOSSIBLE) {
        this._ui.removeAllPickupButtons();
        this._ui.clearPickupDisplay();
        this._ui.dismissToast();
        this._ui.enableButton();
        this._state.clearCarrier();
      } else if (behaviour === PICKUP_BEHAVIOUR.MANDATORY) {
        if (!pickupPoint) {
          this._ui.disableButton();
          this._ui.showMandatoryToastOnce(carrierId);
        } else {
          this._ui.enableButton();
          jQuery("#pickup-required-notice").remove();
        }
      } else {
        // Optional
        this._ui.enableButton();
        jQuery("#pickup-required-notice").remove();
      }

      // ── Pickup button ─────────────────────────────────────────────────────
      if (behaviour !== PICKUP_BEHAVIOUR.IMPOSSIBLE && hasPickupMeta) {
        this._renderPickupButton(selectedRate);
        if (pickupPoint) {
          this._ui.showPickupLabel(pickupPoint.label);
        }
      } else {
        this._ui.removeAllPickupButtons();
      }

      this._lastValidatedCarrierId = carrierId;
      this._lastValidatedRateId    = selectedRate.rate_id;
    } catch (err) {
      console.error("ShiptimizeBlockCheckout.evaluate error:", err);
    }
  }

  _renderPickupButton(selectedRate) {
    this._ui.removeAllPickupButtons();

    const button = document.createElement("button");
    button.type = "button";
    button.className = "button alt shiptimize-pick-location";
    button.textContent =
      window.shiptimize_choose_pickup_location || "Choose Pickup Location";

    button.addEventListener("click", (e) => {
      e.preventDefault();
      this._handlePickupButtonClick(selectedRate);
    });

    // Try to insert next to the selected rate radio — works for standard WC Blocks themes
    const shippingEl = this._findShippingElement(selectedRate.rate_id);
    const insertionPoint = shippingEl
      ? this._findInsertionPoint(shippingEl)
      : null;

    if (insertionPoint) {
      insertionPoint.appendChild(button);
      return;
    }

    // Fallback: inject into the shipping rates package container.
    // Handles themes that wrap the rate list in a non-standard way.
    const fallbacks = [
      ".wc-block-components-shipping-rates-control__package",
      ".wc-block-components-shipping-rates-control",
      ".wp-block-woocommerce-checkout-shipping-methods-block",
      ".wc-block-checkout__shipping-method",
    ];
    for (const sel of fallbacks) {
      const container = document.querySelector(sel);
      if (container) {
        container.appendChild(button);
        return;
      }
    }
  }

  _handlePickupButtonClick(selectedRate) {
    const address = this._state.get("address");
    const required = ["Streetname1", "City", "PostalCode", "Country"];

    if (!required.every((f) => address?.[f]?.trim())) {
      this._ui.showAddressRequiredModal();
      return;
    }

    // Re-confirm carrier ID from the rate in case something changed since evaluate()
    if (selectedRate?.rate_id) {
      const carrierId = this._extractCarrierIdFromMethodId(
        selectedRate.rate_id,
      );
      if (carrierId) this._state.set("carrierId", carrierId);
    }

    if (!this._state.get("carrierId")) {
      this._ui.showToast(
        "<strong>Error:</strong> Could not determine shipping carrier. Please refresh the page.",
        5000,
      );
      return;
    }

    this._onOpenMap();
  }

  _extractAddress() {
    // Primary: read from the WC Blocks cart store.
    // This is always populated — even for guest checkout and cached pages —
    // because WC Blocks keeps the address in client-side JS state, not the DOM.
    const storeAddress = this._extractAddressFromBlocksStore();
    if (storeAddress) {
      this._state.set("address", storeAddress);
      return;
    }

    // Fallback: read from DOM field values (standard WC Blocks field IDs).
    let country = "";
    const countrySelectors = [
      "#components-form-token-input-0",
      "#shipping-country",
      "#billing-country",
      'select[name*="country"]',
    ];
    for (const sel of countrySelectors) {
      const val = jQuery(sel).val();
      if (val?.trim()) {
        country = val;
        break;
      }
    }

    this._state.set("address", {
      Streetname1:
        jQuery("#shipping-address_1").val() ||
        jQuery("#billing-address_1").val() ||
        "",
      Streetname2:
        jQuery("#shipping-address_2").val() ||
        jQuery("#billing-address_2").val() ||
        "",
      City:
        jQuery("#shipping-city").val() || jQuery("#billing-city").val() || "",
      PostalCode:
        jQuery("#shipping-postcode").val() ||
        jQuery("#billing-postcode").val() ||
        "",
      State:
        jQuery("#shipping-state").val() || jQuery("#billing-state").val() || "",
      Country: country,
      Lat: "",
      Long: "",
    });
  }

  /**
   * Read the shipping address directly from the WC Blocks cart store.
   * Works for guest checkout and cached pages — no DOM dependency, no network call.
   * Returns null if the store is unavailable or the address is empty.
   */
  _extractAddressFromBlocksStore() {
    try {
      const store = window.wp?.data?.select("wc/store/cart");
      if (!store?.getCustomerData) return null;

      const customer = store.getCustomerData();

      // WC Blocks exposes shippingAddress; fall back to billingAddress
      const a = customer?.shippingAddress || customer?.billingAddress;
      if (!a?.address_1) return null;

      return {
        Streetname1: a.address_1 || "",
        Streetname2: a.address_2 || "",
        City: a.city || "",
        PostalCode: a.postcode || "",
        State: a.state || "",
        Country: (a.country || "").toUpperCase(),
        Lat: "",
        Long: "",
      };
    } catch {
      return null;
    }
  }

  _extractInitialCarrierId() {
    if (!window.wp?.data) return;
    try {
      const cartData = window.wp.data.select("wc/store/cart").getCartData();
      const selectedRate = cartData?.shippingRates?.[0]?.shipping_rates?.find(
        (r) => r.selected,
      );
      if (selectedRate?.rate_id) {
        const carrierId = this._extractCarrierIdFromMethodId(
          selectedRate.rate_id,
        );
        if (carrierId) this._state.set("carrierId", carrierId);
      }
    } catch {}
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  _extractCarrierIdFromMethodId(methodId) {
    // Match shipping_BRAND_CARRIERID where BRAND is any alphanumeric string.
    // This correctly handles: shiptimize, CTT, GLSDk, CTT_free, DPD, etc.
    // The (\d+) stops at the first non-digit (_free, :instance) so it always
    // captures the carrier ID, never the WooCommerce instance ID.
    const match = (methodId || "").match(
      /shipping_[A-Za-z][A-Za-z0-9]*_(\d+)/i,
    );
    if (match) return parseInt(match[1]);

    // Fallback for non-standard rate ID formats
    const fallback = (methodId || "").match(/[_:](\d+)(?::|$)/);
    return fallback ? parseInt(fallback[1]) : null;
  }

  _findShippingElement(rateId) {
    const selectors = [
      `input[value="${rateId}"]`,
      `input[id*="${rateId}"]`,
      `[data-rate-id="${rateId}"]`,
    ];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  _findInsertionPoint(el) {
    return (
      el.closest("li") ||
      el.closest(".wc-block-components-shipping-rates-control__option") ||
      el.closest("label") ||
      el.parentElement
    );
  }
}
